import React from "react";
import "./ResumeAnalysisModal.css";

const ResumeAnalysisModal = ({ isOpen, onClose, analysisData }) => {
  if (!isOpen || !analysisData) return null;

  const {
    job_family,
    job_level,
    overall_score,
    sectional_grades,
    top_fixes,
    rewrite_suggestions,
    semantic_recommendations,
    ats_findings,
  } = analysisData;

  // Function to download the analysis as a text file
  const handleDownload = () => {
    const content = `
RESUME ANALYSIS REPORT
======================

Job Family: ${job_family}
Job Level: ${job_level}
Overall Score: ${overall_score}/100

SECTIONAL GRADES
----------------
${Object.entries(sectional_grades || {})
  .map(([key, value]) => `${key}: ${value}`)
  .join("\n")}

TOP FIXES
---------
${top_fixes?.map((fix, idx) => `${idx + 1}. ${fix}`).join("\n") || "None"}

REWRITE SUGGESTIONS
------------------
${
  rewrite_suggestions
    ?.map(
      (suggestion, idx) => `
${idx + 1}. Original: "${suggestion.original}"
   Rewrite: "${suggestion.rewrite}"
   Reason: ${suggestion.reason}
`
    )
    .join("\n") || "None"
}

SEMANTIC RECOMMENDATIONS
-----------------------
${
  semantic_recommendations
    ?.map((rec, idx) => `${idx + 1}. ${rec}`)
    .join("\n") || "None"
}

ATS COMPATIBILITY FINDINGS
--------------------------
Multi-Column Layout Detected: ${
      ats_findings?.has_multi_column_layout ? "Yes" : "No"
    }
Contact Info Placement Issue: ${
      ats_findings?.contact_info_placement_issue ? "Yes" : "No"
    }
Non-Standard Characters Detected: ${
      ats_findings?.non_standard_characters_detected ? "Yes" : "No"
    }

Recommendations:
${
  ats_findings?.recommendations
    ?.map((rec, idx) => `${idx + 1}. ${rec}`)
    .join("\n") || "None"
}
    `.trim();

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `resume-analysis-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Function to get score color based on value
  const getScoreColor = (score) => {
    if (score >= 80) return "#10b981";
    if (score >= 60) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <>
      <div className="resume-analysis-overlay" onClick={onClose}></div>
      <div className="resume-analysis-modal">
        <div className="modal-header">
          <h2>Resume Analysis Report</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-content">
          {/* Overall Score Card */}
          <div className="score-card">
            <div className="score-circle-container">
              <svg className="score-circle" viewBox="0 0 120 120">
                <circle
                  className="score-circle-bg"
                  cx="60"
                  cy="60"
                  r="54"
                ></circle>
                <circle
                  className="score-circle-progress"
                  cx="60"
                  cy="60"
                  r="54"
                  style={{
                    strokeDashoffset: 339.292 - (339.292 * overall_score) / 100,
                    stroke: getScoreColor(overall_score),
                  }}
                ></circle>
              </svg>
              <div className="score-text">
                <span className="score-value">{overall_score}</span>
                <span className="score-max">/100</span>
              </div>
            </div>
            <div className="score-details">
              <div className="detail-item">
                <span className="detail-label">Job Family</span>
                <span className="detail-value">{job_family}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Job Level</span>
                <span className="detail-value">{job_level}</span>
              </div>
            </div>
          </div>

          {/* Sectional Grades */}
          <div className="section">
            <h3 className="section-title">Sectional Grades</h3>
            <div className="grades-grid">
              {Object.entries(sectional_grades || {}).map(([key, value]) => (
                <div key={key} className="grade-item">
                  <div className="grade-label">
                    {key.replace(/_/g, " ").toUpperCase()}
                  </div>
                  <div className="grade-badge">{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Fixes */}
          {top_fixes && top_fixes.length > 0 && (
            <div className="section">
              <h3 className="section-title">
                <span className="icon">🔧</span> Top Fixes
              </h3>
              <ul className="fixes-list">
                {top_fixes.map((fix, idx) => (
                  <li key={idx} className="fix-item">
                    {fix}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Rewrite Suggestions */}
          {rewrite_suggestions && rewrite_suggestions.length > 0 && (
            <div className="section">
              <h3 className="section-title">
                <span className="icon">✍️</span> Rewrite Suggestions
              </h3>
              <div className="rewrite-suggestions">
                {rewrite_suggestions.map((suggestion, idx) => (
                  <div key={idx} className="suggestion-card">
                    <div className="suggestion-header">
                      <span className="suggestion-number">#{idx + 1}</span>
                    </div>
                    <div className="suggestion-content">
                      <div className="suggestion-block original">
                        <div className="block-label">Original</div>
                        <div className="block-text">{suggestion.original}</div>
                      </div>
                      <div className="arrow-icon">→</div>
                      <div className="suggestion-block rewrite">
                        <div className="block-label">Rewrite</div>
                        <div className="block-text">{suggestion.rewrite}</div>
                      </div>
                    </div>
                    <div className="suggestion-reason">
                      <strong>Reason:</strong> {suggestion.reason}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Semantic Recommendations */}
          {semantic_recommendations && semantic_recommendations.length > 0 && (
            <div className="section">
              <h3 className="section-title">
                <span className="icon">💡</span> Semantic Recommendations
              </h3>
              <ul className="recommendations-list">
                {semantic_recommendations.map((rec, idx) => (
                  <li key={idx} className="recommendation-item">
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ATS Findings */}
          {ats_findings && (
            <div className="section">
              <h3 className="section-title">
                <span className="icon">🤖</span> ATS Compatibility Check
              </h3>
              <div className="ats-checks">
                <div
                  className={`ats-check ${
                    ats_findings.has_multi_column_layout ? "warning" : "success"
                  }`}
                >
                  <span className="check-icon">
                    {ats_findings.has_multi_column_layout ? "⚠️" : "✓"}
                  </span>
                  <span className="check-text">
                    Multi-Column Layout:{" "}
                    {ats_findings.has_multi_column_layout
                      ? "Detected"
                      : "Not Detected"}
                  </span>
                </div>
                <div
                  className={`ats-check ${
                    ats_findings.contact_info_placement_issue
                      ? "warning"
                      : "success"
                  }`}
                >
                  <span className="check-icon">
                    {ats_findings.contact_info_placement_issue ? "⚠️" : "✓"}
                  </span>
                  <span className="check-text">
                    Contact Info Placement:{" "}
                    {ats_findings.contact_info_placement_issue
                      ? "Issue Found"
                      : "OK"}
                  </span>
                </div>
                <div
                  className={`ats-check ${
                    ats_findings.non_standard_characters_detected
                      ? "warning"
                      : "success"
                  }`}
                >
                  <span className="check-icon">
                    {ats_findings.non_standard_characters_detected ? "⚠️" : "✓"}
                  </span>
                  <span className="check-text">
                    Non-Standard Characters:{" "}
                    {ats_findings.non_standard_characters_detected
                      ? "Detected"
                      : "Not Detected"}
                  </span>
                </div>
              </div>
              {ats_findings.recommendations &&
                ats_findings.recommendations.length > 0 && (
                  <div className="ats-recommendations">
                    <h4 className="ats-rec-title">ATS Recommendations:</h4>
                    <ul className="ats-rec-list">
                      {ats_findings.recommendations.map((rec, idx) => (
                        <li key={idx} className="ats-rec-item">
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="download-btn" onClick={handleDownload}>
            <span className="btn-icon">⬇</span>
            Download Report
          </button>
          <button className="close-footer-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default ResumeAnalysisModal;
